import { useAtom, useSetAtom } from "jotai";
import { useHydrateAtoms } from "jotai/utils";
import type { InferGetStaticPropsType, NextPage } from "next";
import { useEffect, useMemo } from "react";

import { CardCategoryEnum } from "@sorcery/db";
import { LoadingSpinner } from "@sorcery/ui";

import { Page } from "~/components/layouts/Page";
import { FeatureSplashImage } from "~/components/molecules/splashes/FeatureSplashImage";
import {
  avatarJotai,
  deckJotai,
  deckResetterJotai,
  decklistJotai,
  maybeboardJotai,
  sideboardJotai,
} from "~/state/deckJotai";
import { ogPath } from "~/utils/paths";
import { api, jotaiStateDelegationOptions } from "~/utils/trpc";
import { BodyContent } from "./BodyContent";
import { PreconHeader } from "./PreconHeader";
import type { getStaticProps } from "./index.page";

const Precon: NextPage<InferGetStaticPropsType<typeof getStaticProps>> = (props) => {
  /************************************************************************************
   * initialization
   */
  // available instantly due to SSG
  const { deckId } = props;
  const resetJotai = useSetAtom(deckResetterJotai);

  /************************************************************************************
   * data fetching + jotai state hydration
   */
  useHydrateAtoms([[deckJotai, api.useUtils().deck.getById.getData({ id: deckId })]]);
  const [precon, setPrecon] = useAtom(deckJotai);
  // available instantly due to SSG helpers
  api.deck.getById.useQuery(
    { id: deckId },
    {
      ...jotaiStateDelegationOptions,
      select: (data) => {
        setPrecon(data);
        return data;
      },
    },
  );
  const setDecklist = useSetAtom(decklistJotai);
  const { isPending: decklistPending } = api.deck.getDecklistById.useQuery(
    { id: deckId },
    {
      ...jotaiStateDelegationOptions,
      select: (data) => {
        setDecklist(data);
        return data;
      },
    },
  );
  const setAvatar = useSetAtom(avatarJotai);
  const { isPending: avatarPending } = api.deck.getAvatarById.useQuery(
    { id: deckId },
    {
      ...jotaiStateDelegationOptions,
      select: (data) => {
        setAvatar(data);
        return data;
      },
    },
  );
  const setSideboard = useSetAtom(sideboardJotai);
  const { isPending: sideboardPending } = api.deck.getSideboardById.useQuery(
    { id: deckId },
    {
      ...jotaiStateDelegationOptions,
      select: (data) => {
        setSideboard(data);
        return data;
      },
    },
  );
  const setMaybeboard = useSetAtom(maybeboardJotai);
  const { isPending: maybeboardPending } = api.deck.getMaybeboardById.useQuery(
    { id: deckId },
    {
      ...jotaiStateDelegationOptions,
      select: (data) => {
        setMaybeboard(data);
        return data;
      },
    },
  );

  /*************************************************************************************************************************
   * react helpers
   */
  const isLoading = useMemo(
    () => decklistPending || avatarPending || sideboardPending || maybeboardPending,
    [decklistPending, avatarPending, sideboardPending, maybeboardPending],
  );

  // clear deck jotais on unmount
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    return () => {
      resetJotai();
    };
  }, []);

  /*************************************************************************************************************************
   * render
   */
  if (precon) {
    return (
      <>
        {precon.feature && (
          <FeatureSplashImage
            src={precon.feature.src}
            rotate={precon.feature.card.guardian?.category === CardCategoryEnum.Site}
          />
        )}
        <Page
          title={`${precon.name}`}
          description="Official Sorcery TCG Preconstructed Deck"
          image={ogPath(
            precon.feature.src,
            precon.feature.card.guardian?.category === CardCategoryEnum.Site ? 1 : 0,
          )}
        >
          {/* ssr'd skeleton details while full deck data loads */}
          <PreconHeader />

          {isLoading ? (
            <div className="flex h-screen w-full items-center justify-center">
              <LoadingSpinner />
            </div>
          ) : (
            <BodyContent />
          )}
        </Page>
      </>
    );
  }
};

export default Precon;
